<details
  *ngIf="presentation === 'disclosure'; else expandedDetails"
  class="mt-4"
  (toggle)="onToggle($event)"
>
  <summary class="btn btn-link px-0 fw-semibold">{{ summaryLabel }}</summary>

  <ng-container *ngTemplateOutlet="detailsContent"></ng-container>
</details>

<ng-template #expandedDetails>
  <ng-container *ngTemplateOutlet="detailsContent"></ng-container>
</ng-template>

<ng-template #detailsContent>
  <div class="border-top pt-3 mt-2">
    <section aria-label="Complete pricing structure" class="mb-3">
      <h4 class="h6 fw-bold mb-2">How pricing works</h4>
      <div
        *ngFor="let row of result.feeRecipeRows; let first = first"
        class="py-2"
        [class.border-top]="!first"
      >
        <p class="mb-1">{{ row.label }}</p>
        <p class="fw-semibold mb-1">{{ row.recipe }}</p>
        <p class="small text-body-secondary mb-0">
          Paid by {{ row.payerLabel | lowercase }}
        </p>
      </div>
    </section>

    <section class="border-top pt-3 mb-3">
      <h4 class="h6 fw-bold mb-2">Detailed calculation</h4>
      <p class="small text-body-secondary mb-2">
        {{ donationCount | number }} online transactions averaging
        {{ formatMoney(averageDonationCents) }}.
      </p>
      <p
        *ngIf="scenario.dataStatus === 'fixture'"
        class="small text-body-secondary mb-3"
      >
        Development fixture data, not an empirical fundraiser distribution.
      </p>
      <p
        *ngIf="scenario.dataStatus === 'empirical-profile'"
        class="small text-body-secondary mb-3"
      >
        <ng-container
          *ngIf="
            scenario.donationCountSource === 'override';
            else empiricalEstimate
          "
        >
          You set the transaction count. Gift sizes are estimated from one
          anonymized real school fundraiser's card donation pattern.
        </ng-container>
        <ng-template #empiricalEstimate>
          Transaction count and gift sizes are estimated from one anonymized
          real school fundraiser's card donation pattern.
        </ng-template>
      </p>

      <p *ngIf="primaryFeeLineItems.length === 0" class="small mb-0">
        No fee line items are deducted from the intended donations.
      </p>
      <dl *ngIf="primaryFeeLineItems.length" class="row small mb-0">
        <ng-container *ngFor="let item of primaryFeeLineItems">
          <dt class="col-8 fw-normal mb-2">{{ item.label }}</dt>
          <dd class="col-4 text-end mb-2">
            {{ item.amountCents < 0 ? '−' : ''
            }}{{
              formatMoney(
                item.amountCents < 0 ? -item.amountCents : item.amountCents
              )
            }}
          </dd>
        </ng-container>
      </dl>
    </section>

    <section class="border-top pt-3 mb-3">
      <h4 class="h6 fw-bold mb-2">Donor-paid amounts</h4>
      <p *ngIf="result.donorPaidExtraEstimated" class="small mb-1">
        Donors pay
        <strong>{{ formatMoney(result.donorPaidExtraCents) }}</strong>
        beyond their intended donations.
      </p>
      <p *ngIf="!result.donorPaidExtraEstimated" class="small mb-1">
        Optional donor-paid amounts are not estimated.
      </p>
      <p
        *ngIf="result.donorPaidExtraNote"
        class="small text-body-secondary mb-0"
      >
        {{ result.donorPaidExtraNote }}
      </p>
    </section>

    <section *ngIf="result.annualCostCents > 0" class="border-top pt-3 mb-3">
      <h4 class="h6 fw-bold mb-2">Annual costs</h4>
      <p class="small mb-0">
        Annual platform cost shown separately:
        <strong>{{ formatMoney(result.annualCostCents) }}</strong>
      </p>
    </section>

    <div
      *ngIf="result.warnings.length"
      class="alert alert-secondary small py-2"
    >
      <p
        *ngFor="let warning of result.warnings; let last = last"
        class="mb-1"
        [class.mb-0]="last"
      >
        {{ warning }}
      </p>
    </div>

    <div *ngIf="result.publishedFacts.length" class="mb-3">
      <div
        *ngFor="let fact of result.publishedFacts"
        class="border rounded-3 p-3"
      >
        <div class="d-flex flex-wrap gap-2 align-items-center mb-1">
          <p class="fw-semibold mb-0">{{ fact.value }}</p>
          <span
            class="badge border"
            [ngClass]="evidenceBadgeClasses(fact.evidenceStatus)"
          >
            {{ fact.label }}
          </span>
        </div>
        <p class="small mb-0">{{ fact.explanation }}</p>
      </div>
    </div>

    <div class="small mb-3">
      <p class="fw-semibold mb-1">Pricing scope</p>
      <p class="mb-2">{{ result.regionLabel }}</p>
      <ul *ngIf="result.qualifications.length" class="ps-3 mb-2">
        <li *ngFor="let qualification of result.qualifications" class="mb-1">
          {{ qualification }}
        </li>
      </ul>
      <ul *ngIf="result.disclosures.length" class="ps-3 mb-0">
        <li *ngFor="let disclosure of result.disclosures" class="mb-1">
          {{ disclosure }}
        </li>
      </ul>
    </div>

    <div class="small text-body-secondary">
      <p class="mb-2">
        <strong>{{ result.evidence.label }}:</strong>
        {{ result.evidence.description }}
      </p>
      <p class="fw-semibold mb-1">Pricing sources</p>
      <p *ngIf="result.sources.length === 0" class="mb-0">
        No source is attached to the selected terms. This result uses the values
        you entered.
      </p>
      <ul *ngIf="result.sources.length" class="ps-3 mb-0">
        <li *ngFor="let source of result.sources" class="mb-2">
          <a
            *ngIf="source.url; else sourceWithoutLink"
            [href]="source.url"
            target="_blank"
            rel="noopener"
            (click)="onPricingSourceOpened(source.id)"
          >
            {{ source.label }}
          </a>
          <ng-template #sourceWithoutLink>{{ source.label }}</ng-template>
          <span *ngIf="source.documentDate">
            · Dated {{ source.documentDate }}
          </span>
          <span>· Checked {{ source.verifiedOn }}</span>
          <span class="d-block">{{ source.coverageNotes }}</span>
        </li>
      </ul>
    </div>
  </div>
</ng-template>
